// Imperative confirmation API. <ConfirmHost/> (mounted once in App.js) renders
// the dialog; callers just await the answer:
//
//   if (!(await confirmDelete('tender', tender.name))) return;
//   await api.delete(...);
//
// Resolves `false` when the user cancels (or no host is mounted, so a missing
// host can never silently confirm), `true` when they confirm, or the trimmed
// reason string when `reasonLabel` is set.
//
// Pass `onConfirm` to run the work inside the dialog: it stays open with a
// spinner and blocks repeat clicks until the work settles. Handle errors in
// that callback (e.g. notify.apiError); a throw closes the dialog and resolves
// `false`.

let host = null;

export const registerConfirmHost = fn => {
  host = fn;
  return () => {
    if (host === fn) host = null;
  };
};

export const confirmAction = options =>
  new Promise(resolve => {
    if (!host) {
      // eslint-disable-next-line no-console
      console.error('confirmAction: no <ConfirmHost/> mounted.');
      resolve(false);
      return;
    }
    host({ ...options, resolve });
  });

const withEntity = (entity, fallback) => entity || fallback;

// Permanently removes a record.
export const confirmDelete = (entity, subject, options = {}) =>
  confirmAction({
    tone: 'danger',
    title: `Delete this ${withEntity(entity, 'record')}?`,
    message: 'It will be permanently deleted. This cannot be undone.',
    subject,
    confirmLabel: `Delete ${withEntity(entity, 'record')}`,
    ...options,
  });

// Detaches an item from the screen/parent (row in a table, attachment, ...).
export const confirmRemove = (entity, subject, options = {}) =>
  confirmAction({
    tone: 'danger',
    title: `Remove this ${withEntity(entity, 'item')}?`,
    message: 'It will be removed. This cannot be undone.',
    subject,
    confirmLabel: `Remove ${withEntity(entity, 'item')}`,
    ...options,
  });

// Final submission that locks or sends data on.
export const confirmSubmit = (entity, subject, options = {}) =>
  confirmAction({
    tone: 'primary',
    title: `Submit this ${withEntity(entity, 'form')}?`,
    message:
      'Once submitted it is sent on for processing and can no longer be edited.',
    subject,
    confirmLabel: `Submit ${withEntity(entity, 'form')}`,
    ...options,
  });

// Rejection / decline; the reason is recorded and resolved as a string.
export const confirmReject = (entity, subject, options = {}) =>
  confirmAction({
    tone: 'danger',
    title: `Reject this ${withEntity(entity, 'request')}?`,
    message: 'The requester will be notified. This cannot be undone.',
    subject,
    confirmLabel: `Reject ${withEntity(entity, 'request')}`,
    reasonLabel: 'Reason for rejection',
    reasonRequired: true,
    ...options,
  });

// Cancels a process that is already running. Cancel label stays "Keep it" so
// "Cancel" never means two opposite things in the same dialog.
export const confirmCancelProcess = (entity, subject, options = {}) =>
  confirmAction({
    tone: 'warning',
    title: `Cancel this ${withEntity(entity, 'process')}?`,
    message: 'Progress will be stopped. This cannot be undone.',
    subject,
    confirmLabel: `Cancel ${withEntity(entity, 'process')}`,
    cancelLabel: `Keep ${withEntity(entity, 'process')}`,
    ...options,
  });
